export interface StoredAgentVisual {
  icon?: string
  colorClass?: string
  coverUrl?: string
}

const STORAGE_KEY = 'agent-visuals-v1'

export function loadAgentVisualsMap(): Record<string, StoredAgentVisual> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, StoredAgentVisual>) : {}
  } catch {
    return {}
  }
}

export function saveAgentVisual(agentId: string, visual: StoredAgentVisual) {
  if (typeof window === 'undefined' || !agentId) return
  const map = loadAgentVisualsMap()
  const next: StoredAgentVisual = {
    icon: visual.icon || undefined,
    colorClass: visual.colorClass || undefined,
    coverUrl: visual.coverUrl || undefined,
  }
  if (!next.icon && !next.coverUrl) {
    delete map[agentId]
  } else {
    map[agentId] = next
  }

  const persist = (payload: Record<string, StoredAgentVisual>): boolean => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
      return true
    } catch {
      return false
    }
  }

  // First attempt: persist full payload.
  if (persist(map)) return

  // Fallback 1: if quota is exceeded, keep cover image only for current agent;
  // strip cover images from the rest while preserving their icons/colors.
  const compacted: Record<string, StoredAgentVisual> = {}
  Object.entries(map).forEach(([id, item]) => {
    if (!item) return
    if (id === agentId) {
      compacted[id] = item
      return
    }
    const iconOnly: StoredAgentVisual = {
      icon: item.icon || undefined,
      colorClass: item.colorClass || undefined,
    }
    if (iconOnly.icon || iconOnly.colorClass) {
      compacted[id] = iconOnly
    }
  })
  if (persist(compacted)) return

  // Fallback 2: final safety net, keep only the current agent visual (or icon only).
  const minimalCurrent: StoredAgentVisual = next.coverUrl
    ? next
    : { icon: next.icon || undefined, colorClass: next.colorClass || undefined }
  persist(
    minimalCurrent.icon || minimalCurrent.coverUrl
      ? { [agentId]: minimalCurrent }
      : {}
  )
}
